import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { auth } from '../api/client'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [trainer, setTrainer] = useState(auth.getStoredTrainer())
  const [isLoading, setIsLoading] = useState(true)

  // Validate session on mount with mounted flag to prevent state updates after unmount
  useEffect(() => {
    let mounted = true

    async function validateSession() {
      setIsLoading(true)
      try {
        const data = await auth.getSession()
        if (!mounted) return
        if (data?.success) {
          setTrainer(data.trainer)
        } else {
          setTrainer(null)
        }
      } catch (error) {
        console.error('Session validation failed:', error)
        if (!mounted) return
        setTrainer(null)
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    validateSession()

    return () => {
      mounted = false
    }
  }, [])

  const login = useCallback(async (email, password) => {
    const data = await auth.login(email, password)
    if (data.success) {
      setTrainer(data.trainer)
    }
    return data
  }, [])

  const logout = useCallback(async () => {
    await auth.logout()
    setTrainer(null)
  }, [])

  const updateTrainer = useCallback((updatedTrainer) => {
    setTrainer(updatedTrainer)
  }, [])

  const value = {
    trainer,
    isAuthenticated: !!trainer,
    isLoading,
    login,
    logout,
    updateTrainer,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Protected Route wrapper
export function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Redirect to login via hash router
    window.location.hash = '#/login'
    return null
  }

  return children
}
